import type { FormListInterface } from '@/modules/core/models/types'
import InsuranceAnotherCompanyDataForm from '@/modules/common/hiring/views/forms/InsuranceAnotherCompanyDataForm.vue'
import InsurancePersonalDataStep1Form from '@/modules/common/hiring/views/forms/InsurancePersonalDataStep1Form.vue'
import InsurancePersonalDataStep2Form from '@/modules/common/hiring/views/forms/InsurancePersonalDataStep2Form.vue'
import InsurancePersonalDataStep3Form from '@/modules/common/hiring/views/forms/InsurancePersonalDataStep3Form.vue'
import InsurancePersonalDataStep4Form from '@/modules/common/hiring/views/forms/InsurancePersonalDataStep4Form.vue'
import InsurancePersonalDataStep5Form from '@/modules/common/hiring/views/forms/InsurancePersonalDataStep5Form.vue'
import TravelInsuranceProcessStepEnum from '@/modules/travel/hiring/models/APP/TravelInsuranceProcessStepEnum'
import { UserRoleEnum } from '@/modules/core/models/enums'
import TravelPaymentTypeForm from '@/modules/travel/hiring/views/forms/TravelPaymentTypeForm.vue'
import Step0Form from '../views/forms/Step0Form.vue'
import type TravelHiringAnswers from '../models/APP/TravelHiringAnswers'
import { isBigger } from '@/utils/date.utils'

export const STEP0_PAGES: FormListInterface = {
  Step0_1_StartDateForm: {
    form: Step0Form,
    next: ['InsuranceAnotherCompanyDataForm'],
    extra: {
      step: 0,
      showActions: false,
      processStep: [TravelInsuranceProcessStepEnum.RATER]
    }
  }
}

export const STEP1_PAGES: FormListInterface = {
  Step1_1_InsuranceAnotherCompanyDataForm: {
    // dynamic pagination will replace this for something like 'Step1_0_1_InsuranceAnotherCompanyDataForm'
    // the 0 is the insured index position
    form: InsuranceAnotherCompanyDataForm,
    next: ['Step1_3_InsurancePersonalDataStep1Form'],
    extra: {
      step: 1,
      showDeclaration: true,
      roles: [UserRoleEnum.HOLDER_INSURED, UserRoleEnum.HOLDER_NOT_INSURED, UserRoleEnum.INSURED],
      processStep: [
        TravelInsuranceProcessStepEnum.POLICY_HOLDER_FIRST,
        TravelInsuranceProcessStepEnum.DOCUMENTS,
        TravelInsuranceProcessStepEnum.INSUREDS
      ],
      onSubmit: 'sendPolicyHolderDocument',
      summarySubStepIndex: 0
    }
  },
  Step1_3_InsurancePersonalDataStep1Form: {
    form: InsurancePersonalDataStep1Form,
    next: ['Step1_4_InsurancePersonalDataStep2Form'],
    extra: {
      step: 1,
      needsBirthdate: true,
      roles: [UserRoleEnum.HOLDER_INSURED, UserRoleEnum.HOLDER_NOT_INSURED, UserRoleEnum.INSURED],
      processStep: [TravelInsuranceProcessStepEnum.POLICY_HOLDER_SECOND],
      onSubmit: 'checkExistClient',
      summarySubStepIndex: 1
    }
  },
  Step1_4_InsurancePersonalDataStep2Form: {
    form: InsurancePersonalDataStep2Form,
    next: ['Step1_5_InsurancePersonalDataStep3Form'],
    extra: {
      step: 1,
      roles: [UserRoleEnum.HOLDER_INSURED, UserRoleEnum.HOLDER_NOT_INSURED, UserRoleEnum.INSURED],
      summarySubStepIndex: 2
    }
  },
  Step1_5_InsurancePersonalDataStep3Form: {
    form: InsurancePersonalDataStep3Form,
    next: ['Step1_6_InsurancePersonalDataStep4Form'],
    extra: {
      showActions: false,
      step: 1,
      roles: [UserRoleEnum.HOLDER_INSURED, UserRoleEnum.HOLDER_NOT_INSURED, UserRoleEnum.INSURED],
      onSubmit: 'sendYoungInsured', //? only for young insureds
      summarySubStepIndex: 2
    }
  },
  Step1_6_InsurancePersonalDataStep4Form: {
    form: InsurancePersonalDataStep4Form,
    next: ['Step1_7_InsurancePersonalDataStep5Form'],
    extra: {
      step: 1,
      roles: [UserRoleEnum.HOLDER_INSURED, UserRoleEnum.HOLDER_NOT_INSURED, UserRoleEnum.INSURED],
      onSubmit: 'sendInsureds',
      runtimeGuard: (answers: TravelHiringAnswers, direction: 'next' | 'prev') => {
        const insured = answers.currentInsured!
        if (insured.role !== UserRoleEnum.INSURED) {
          return { canAccess: true }
        }

        const birthDate = insured.userDetails.birthDate
        if (!birthDate) {
          return { canAccess: true }
        }

        if (isBigger(birthDate, 18)) {
          return { canAccess: true }
        }

        // El asegurado es menor de edad
        const currentRiskNumber = insured.riskNumber
        const totalInsureds = answers.forWhomPeople
        const isLastInsured = currentRiskNumber === totalInsureds

        if (isLastInsured) {
          // Es el último asegurado menor de edad, ir directamente a pago
          return {
            canAccess: false,
            redirectTo: direction === 'next' ? 'Step2_1_PaymentType' : undefined
          }
        }

        // No es el último asegurado, ir al siguiente asegurado
        const nextPageName = `Step1_${currentRiskNumber}_1_InsuranceAnotherCompanyDataForm`

        return {
          canAccess: false,
          redirectTo: direction === 'next' ? nextPageName : undefined
        }
      },
      summarySubStepIndex: 3
    }
  },
  Step1_7_InsurancePersonalDataStep5Form: {
    form: InsurancePersonalDataStep5Form,
    next: ['Step2_1_PaymentType'],
    extra: {
      step: 1,
      hideInsuredSubtitle: true,
      roles: [UserRoleEnum.HOLDER_INSURED, UserRoleEnum.HOLDER_NOT_INSURED],
      onSubmit: 'sendPolicyHolder',
      summarySubStepIndex: 3
    }
  }
}

export const STEP2_PAGES: FormListInterface = {
  Step2_1_PaymentType: {
    form: TravelPaymentTypeForm,
    next: [],
    extra: {
      step: 2,
      onSubmit: 'goToPayment',
      hideInsuredSubtitle: true,
      showActions: false,
      processStep: [TravelInsuranceProcessStepEnum.PAYMENT]
    }
  }
}

export default {
  STEP0_PAGES,
  STEP1_PAGES,
  STEP2_PAGES
}
